import React, { useCallback, useEffect, useState } from 'react';
import { isClient } from '../lib/helpers/index';
import {
	ProposalsIcon,
	DashboardIcon,
	ContractsIcon,
	ContactsIcon,
	InvoicesIcon,
	ClientFoldersIcon,
	CompanyFoldersIcon,
	ResourcesIcon,
	VendorsIcon,
	InquiryIcon,
	ExportIcon,
} from '../mui/Icons/index';
import { Routes } from './routes';
import { CustomListItemType } from '../blocks/Page/CustomListItem/CustomListItemComponent';
import TimelinesIcon from '../mui/Icons/TimelinesIcon';
import { gql } from '@apollo/client';
import {
	TeamMemberRole,
	useGetNavigationItems_GetOrgUserWhereQuery,
	UserType,
} from '#types';
import { FlattenedUser } from '../types/user';
import { useSelector } from 'react-redux';
import rpcShared from '@rockpapercoin/rpc-shared';
import { useToast } from '../hooks/useToast';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getNavigationItems_getOrgUserWhere = gql`
	query getNavigationItems_getOrgUserWhere($where: OrgUserWhereUniqueInput!) {
		getOrgUserWhere(where: $where) {
			id
			organization {
				id
				createdAt
				subscription {
					id
					status
					subscriptionPlan {
						id
						type
					}
				}
				groups {
					id
					name
				}
			}
		}
	}
`;

export enum MenuItemsIds {
	Dashboard = 'dashboard',
	Contacts = 'contacts',
	Proposals = 'proposals',
	Contracts = 'contracts',
	Invoices = 'invoices',
	Vendors = 'vendors',
	CustomerFolders = 'customerFolders',
	CompanyFolders = 'companyFolders',
	MyFolder = '/myFolder',
	Resources = 'resources',
	Profile = 'profile',
	Admin = 'admin',
	Wallet = 'wallet',
	ClientUsers = 'clientUsers',
	OrgUsers = 'orgUsers',
	Organizations = 'organizations',
	OrgOnboarding = 'orgOnboarding',
	ClientOnboarding = '/clientOnboarding',
	OrgMemberSignUp = 'orgMemberSignUp',
	Timelines = 'timelines',
	Leads = 'leads',
	Files = 'files',
	Reports = 'reports',
}

export type NavigationItems = { [index: string]: CustomListItemType };

const useGetNavigationItems = () => {
	const user: FlattenedUser = useSelector( ( state: any ) => state?.user );
	const { showError } = useToast();
	const orgUser =
		user?.isLoggedIn && user.userType === UserType.OrgUser ? user : undefined;
	const { data, error } = useGetNavigationItems_GetOrgUserWhereQuery( {
		variables: { where: { id: orgUser?.id || '' } },
		skip: !orgUser?.id,
	} );
	const isUserOrgAdminOrManager = orgUser
		? rpcShared.organizations.isAdminOrManager( orgUser.organization, orgUser )
		: false;
	const isUserOrgUser = !!orgUser;
	const isUserClient = user?.isLoggedIn ? isClient( user ) : false;
	const isUserSuperAdmin = user && 'isSuperAdmin' in user && user.isSuperAdmin;
	const canViewLeads = rpcShared.subscriptions.canAccessPremium(
		data?.getOrgUserWhere?.organization
	);

	const [ subscriptionNeededModalOpen, setSubscriptionNeededModalOpen ] =
		useState( false );

	const closeSubscriptionNeededModal = useCallback(
		() => setSubscriptionNeededModalOpen( false ),
		[]
	);

	useEffect( () => {
		if ( error ) {
			showError( error );
		}
	}, [ error, showError ] );

	const menuItems: NavigationItems = {
		[ MenuItemsIds.Dashboard ]: {
			id: 'dashboard',
			label: 'Dashboard',
			href: Routes.Dashboard,
			icon: <DashboardIcon />,
			isDisabled: isUserSuperAdmin,
		},
		[ MenuItemsIds.Contacts ]: {
			id: 'clients',
			label: 'Clients',
			href: Routes.Clients,
			icon: <ContactsIcon />,
			isVisible: isUserOrgAdminOrManager || isUserOrgUser,
		},
		[ MenuItemsIds.Proposals ]: {
			id: 'proposals',
			label: 'Proposals',
			href: Routes.Proposals,
			icon: <ProposalsIcon />,
			isDisabled: isUserSuperAdmin,
		},
		[ MenuItemsIds.Contracts ]: {
			id: 'contracts',
			label: 'Contracts',
			href: Routes.Contracts,
			icon: <ContractsIcon />,
			isDisabled: isUserSuperAdmin,
		},
		[ MenuItemsIds.Invoices ]: {
			id: 'invoices',
			label: 'Invoices',
			href: Routes.Invoices,
			icon: <InvoicesIcon />,
		},
		[ MenuItemsIds.Vendors ]: {
			id: 'vendors',
			label: 'Vendors',
			href: '/vendors',
			isVisible: isUserClient,
			icon: <VendorsIcon />,
		},
		[ MenuItemsIds.ClientUsers ]: {
			id: 'clientUsers',
			label: 'Client Users',
			href: '/ClientUsers',
			isVisible: isUserSuperAdmin,
		},
		[ MenuItemsIds.OrgUsers ]: {
			id: 'orgUsers',
			label: 'Org Users',
			href: '/OrgUsers',
			isVisible: isUserSuperAdmin,
		},
		[ MenuItemsIds.Organizations ]: {
			id: 'organizations',
			label: 'Organizations',
			href: '/Organizations',
			isVisible: isUserSuperAdmin,
		},

		[ MenuItemsIds.CustomerFolders ]: {
			id: 'customerFolders',
			label: 'Client Folders',
			href: Routes.CustomerFolders,
			icon: <ClientFoldersIcon />,
			isDisabled: !isUserOrgUser,
		},
		[ MenuItemsIds.CompanyFolders ]: {
			id: 'companyFolders',
			label: 'Company Folders',
			href: Routes.CompanyFolders,
			icon: <CompanyFoldersIcon />,
			isVisible: isUserOrgAdminOrManager,
		},
		[ MenuItemsIds.MyFolder ]: {
			id: 'myFolder',
			label:
				isUserClient || orgUser?.role === TeamMemberRole.Member
					? 'Folder'
					: 'Folders',
			href: Routes.MyFolder,
			icon: <ClientFoldersIcon />,
		},
		[ MenuItemsIds.Resources ]: {
			id: 'resources',
			label: 'Resources',
			href: Routes.Resources,
			icon: <ResourcesIcon />,
			isVisible: isUserOrgAdminOrManager,
		},
		[ MenuItemsIds.Timelines ]: {
			id: 'timelines',
			href: '/timelines',
			label: 'Timeline Genius',
			isVisible: isUserOrgUser,
			icon: <TimelinesIcon />,
		},
		[ MenuItemsIds.Leads ]: {
			id: 'leads',
			...( !canViewLeads
				? {
					onClick: () => setSubscriptionNeededModalOpen( true ),
				  }
				: {
					href: '/leads',
				  } ),
			label: 'Leads',
			isVisible: isUserOrgAdminOrManager,
			icon: <InquiryIcon />,
		},
		[ MenuItemsIds.Files ]: {
			id: 'files',
			label: 'Files',
			href: Routes.Files,
			icon: <ClientFoldersIcon />,
			isVisible: true,
		},
		[ MenuItemsIds.Reports ]: {
			id: 'reports',
			label: 'Reports',
			href: Routes.Reports,
			icon: <ExportIcon />,
			isVisible: isUserOrgUser,
		},
	};

	return {
		items: menuItems,
		subscriptionNeededModalOpen,
		closeSubscriptionNeededModal,
	};
};

export default useGetNavigationItems;
